import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { BoekhoudgemakPanel } from "../../components/BoekhoudgemakPanel";
import { GoogleDrivePanel } from "../../components/GoogleDrivePanel";
import { BottomNav } from "../../components/BottomNav";
import { StatementCycleChart } from "../../components/StatementCycleChart";
import { formatDate, formatMoney } from "../../lib/format";
import type {
  BatchStatementPreview,
  BatchStatementResult,
  ImportBatch,
  StatementCycle,
  StatementMatchPreview,
} from "../../types";

/** Parse Statement-20260609 / 2026-06-09 into ISO date. */
function parseStatementDate(raw: string): string | null {
  const m = raw.trim().match(/(?:Statement[-_])?(\d{4})[-_/]?(\d{2})[-_/]?(\d{2})/i);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + months, d));
  return dt.toISOString().slice(0, 10);
}

function defaultStartFromEnd(endIso: string): string {
  // Statement-20260609 ≈ 2026-05-10 → 2026-06-09
  const prev = addMonths(endIso, -1);
  const [y, m, d] = prev.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  return dt.toISOString().slice(0, 10);
}

function statementLabel(endIso: string): string {
  return `Statement-${endIso.replace(/-/g, "")}`;
}

export function AdminImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [statementRef, setStatementRef] = useState("Statement-");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [filterToPeriod, setFilterToPeriod] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [preview, setPreview] = useState<StatementMatchPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [attachBatch, setAttachBatch] = useState<ImportBatch | null>(null);
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [attachPreview, setAttachPreview] = useState<BatchStatementPreview | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [attachBusy, setAttachBusy] = useState(false);
  const [cycleId, setCycleId] = useState<number | null>(null);
  const [cycleNonce, setCycleNonce] = useState(0);
  const [cycle, setCycle] = useState<StatementCycle | null>(null);
  const [cycleError, setCycleError] = useState<string | null>(null);

  async function loadBatches() {
    const list = await api.request<ImportBatch[]>("/imports");
    setBatches(list);
  }

  useEffect(() => {
    loadBatches().catch(console.error);
  }, []);

  useEffect(() => {
    if (cycleId != null || !batches.length) return;
    const first = batches.find(
      (b) => b.source !== "boekhoudgemak" && b.period_start && b.period_end,
    );
    if (first) setCycleId(first.id);
  }, [batches, cycleId]);

  useEffect(() => {
    if (cycleId == null) {
      setCycle(null);
      return;
    }
    let cancelled = false;
    setCycleError(null);
    api
      .request<StatementCycle>(`/imports/${cycleId}/cycle`)
      .then((res) => {
        if (!cancelled) setCycle(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCycle(null);
          setCycleError(err instanceof Error ? err.message : "Could not load the cycle");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cycleId, cycleNonce]);

  useEffect(() => {
    if (!file || !pdfFile) {
      setPreview(null);
      setPreviewError(null);
      setPreviewBusy(false);
      return;
    }
    let cancelled = false;
    setPreviewBusy(true);
    setPreviewError(null);
    const form = new FormData();
    form.append("file", file);
    form.append("statement_pdf", pdfFile);
    api
      .request<StatementMatchPreview>("/imports/csv/match-preview", {
        method: "POST",
        body: form,
        headers: {},
      })
      .then((res) => {
        if (cancelled) return;
        setPreview(res);
        if (res.period_label) setStatementRef(res.period_label);
        if (res.period_start) setPeriodStart(res.period_start);
        if (res.period_end) setPeriodEnd(res.period_end);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(err instanceof Error ? err.message : "Could not read the PDF");
      })
      .finally(() => {
        if (!cancelled) setPreviewBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [file, pdfFile]);

  const derivedLabel = useMemo(() => {
    const end = periodEnd || parseStatementDate(statementRef);
    return end ? statementLabel(end) : null;
  }, [statementRef, periodEnd]);

  function onStatementRefChange(value: string) {
    setStatementRef(value);
    const iso = parseStatementDate(value);
    if (iso) {
      setPeriodEnd(iso);
      setPeriodStart(defaultStartFromEnd(iso));
    }
  }

  function onPeriodEndChange(value: string) {
    setPeriodEnd(value);
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setStatementRef(statementLabel(value));
      if (!periodStart) setPeriodStart(defaultStartFromEnd(value));
    }
  }

  async function onAttachFile(batch: ImportBatch, file: File | null) {
    setAttachBatch(batch);
    setAttachFile(file);
    setAttachPreview(null);
    setAttachError(null);
    setMessage(null);
    if (!file) return;
    setAttachBusy(true);
    try {
      const form = new FormData();
      form.append("statement_pdf", file);
      const res = await api.request<BatchStatementPreview>(
        `/imports/${batch.id}/statement-pdf/preview`,
        { method: "POST", body: form, headers: {} },
      );
      setAttachPreview(res);
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : "Could not read the PDF");
    } finally {
      setAttachBusy(false);
    }
  }

  async function applyAttach() {
    if (!attachBatch || !attachFile) return;
    setAttachBusy(true);
    setAttachError(null);
    try {
      const form = new FormData();
      form.append("statement_pdf", attachFile);
      const res = await api.request<BatchStatementResult>(
        `/imports/${attachBatch.id}/statement-pdf`,
        { method: "POST", body: form, headers: {} },
      );
      setMessage(res.message);
      setCycleId(attachBatch.id);
      setCycleNonce((n) => n + 1);
      setAttachBatch(null);
      setAttachFile(null);
      setAttachPreview(null);
      await loadBatches();
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : "Could not attach the PDF");
    } finally {
      setAttachBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("source", "ing_mastercard");
      if (statementRef.trim()) form.append("statement_ref", statementRef.trim());
      if (derivedLabel) form.append("period_label", derivedLabel);
      if (periodStart) form.append("period_start", periodStart);
      if (periodEnd) form.append("period_end", periodEnd);
      form.append("filter_to_period", filterToPeriod ? "true" : "false");
      if (pdfFile) form.append("statement_pdf", pdfFile);
      const res = await api.request<{ message: string; batch: ImportBatch }>(
        "/imports/csv",
        { method: "POST", body: form, headers: {} },
      );
      setMessage(res.message);
      setFile(null);
      setPdfFile(null);
      setPreview(null);
      setFormKey((k) => k + 1);
      await loadBatches();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell admin">
      <header className="topbar">
        <h2 style={{ fontSize: "1.35rem" }}>Import transactions</h2>
      </header>

      <form className="panel stack" onSubmit={onSubmit}>
        <p className="muted" style={{ margin: 0 }}>
          Upload the portal CSV together with the official ING statement (
          <code>Statement-20260609.pdf</code>
          ). The PDF picks the cycle and, when its transaction list is readable, the
          exact CSV rows on that statement. Duplicates already imported are skipped.
        </p>
        <div className="field">
          <label htmlFor="csv">CSV file</label>
          <input
            id="csv"
            key={`csv-${formKey}`}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="statement-pdf">Official statement PDF</label>
          <input
            id="statement-pdf"
            key={`pdf-${formKey}`}
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
          />
          <span className="muted" style={{ fontSize: "0.8rem" }}>
            Optional. Without it, type the statement date below.
          </span>
        </div>
        {previewBusy && <p className="muted">Matching the PDF to the CSV…</p>}
        {previewError && <div className="alert">{previewError}</div>}
        {preview && (
          <div className="stack" style={{ gap: "0.5rem" }}>
            <div className="alert alert-ok">{preview.message}</div>
            <StatementCycleChart
              days={preview.days}
              periodStart={preview.period_start}
              periodEnd={preview.period_end}
            />
            {(preview.included.length > 0 || preview.left_out_sample.length > 0) && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>On statement</th>
                      <th>Date</th>
                      <th>Merchant</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.included.map((row, i) => (
                      <tr key={`in-${row.date}-${row.merchant}-${i}`}>
                        <td>Yes</td>
                        <td>{formatDate(row.date)}</td>
                        <td>{row.merchant}</td>
                        <td>{formatMoney(row.amount)}</td>
                      </tr>
                    ))}
                    {preview.left_out_sample.map((row, i) => (
                      <tr key={`out-${row.date}-${row.merchant}-${i}`}>
                        <td className="muted">No</td>
                        <td className="muted">{formatDate(row.date)}</td>
                        <td className="muted">{row.merchant}</td>
                        <td className="muted">{formatMoney(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {preview.included.length < preview.matched && (
              <span className="muted" style={{ fontSize: "0.8rem" }}>
                Showing {preview.included.length} of {preview.matched} matching rows.
              </span>
            )}
          </div>
        )}
        <div className="field">
          <label htmlFor="statement">Statement (match PDF name)</label>
          <input
            id="statement"
            placeholder="Statement-20260609 or Statement-20260609.pdf"
            value={statementRef}
            disabled={!!pdfFile}
            onChange={(e) => onStatementRefChange(e.target.value)}
          />
          {derivedLabel && (
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              Import label: <strong>{derivedLabel}</strong>
            </span>
          )}
        </div>
        <div className="row" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: "10rem" }}>
            <label htmlFor="start">Period from</label>
            <input
              id="start"
              type="date"
              value={periodStart}
              disabled={!!pdfFile}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: "10rem" }}>
            <label htmlFor="end">Period to (statement date)</label>
            <input
              id="end"
              type="date"
              value={periodEnd}
              disabled={!!pdfFile}
              onChange={(e) => onPeriodEndChange(e.target.value)}
            />
          </div>
        </div>
        <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={filterToPeriod}
            onChange={(e) => setFilterToPeriod(e.target.checked)}
          />
          <span>
            {pdfFile && preview?.method === "lines"
              ? "Only import CSV rows that appear on this statement"
              : pdfFile
                ? "Only import CSV rows inside the period read from the PDF"
                : "Only import CSV rows inside this statement period"}
          </span>
        </label>
        {error && <div className="alert">{error}</div>}
        {message && <div className="alert alert-ok">{message}</div>}
        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || !file || previewBusy || (!!pdfFile && !!previewError)}
        >
          {busy ? "Importing…" : "Import transactions"}
        </button>
      </form>

      <BoekhoudgemakPanel onImported={() => loadBatches().catch(console.error)} />
      <GoogleDrivePanel />

      <section className="panel stack" style={{ marginTop: "1rem" }}>
        <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Recent imports</h3>
        <p className="muted" style={{ margin: 0 }}>
          Already imported the CSV? Attach the official statement PDF on that row.
          Transactions printed on the PDF stay with that statement. The rest move to a
          new import (<code>Outside Statement-…</code>), receipts still attached, ready
          for the next PDF.
        </p>
        {cycleError && <div className="alert">{cycleError}</div>}
        {cycle && (
          <StatementCycleChart
            title={`${cycle.period_label || cycle.filename} · ${cycle.transaction_count} transactions`}
            days={cycle.days}
            periodStart={cycle.period_start}
            periodEnd={cycle.period_end}
          />
        )}
        {attachBusy && <p className="muted">Reading the statement…</p>}
        {attachError && <div className="alert">{attachError}</div>}
        {attachPreview && (
          <div className="stack" style={{ gap: "0.5rem" }}>
            <div className="alert alert-ok">{attachPreview.message}</div>
            <StatementCycleChart
              days={attachPreview.days}
              periodStart={attachPreview.period_start}
              periodEnd={attachPreview.period_end}
            />
            {attachPreview.receipts_left_out > 0 && (
              <p className="muted" style={{ margin: 0 }}>
                {attachPreview.receipts_left_out} transaction
                {attachPreview.receipts_left_out === 1 ? "" : "s"} moving off this
                statement already {attachPreview.receipts_left_out === 1 ? "has" : "have"} a
                receipt. The receipt stays linked.
              </p>
            )}
            <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={attachBusy || attachPreview.matched === 0}
                onClick={applyAttach}
              >
                Apply to {attachPreview.filename}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={attachBusy}
                onClick={() => {
                  setAttachPreview(null);
                  setAttachFile(null);
                  setAttachBatch(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>When</th>
                <th>File</th>
                <th>Statement</th>
                <th>Cycle</th>
                <th>Imported</th>
                <th>Skipped</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className={b.id === cycleId ? "cycle-selected" : undefined}>
                  <td>{new Date(b.created_at).toLocaleString()}</td>
                  <td>{b.filename}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setCycleId(b.id)}
                    >
                      {b.period_label || "Show cycle"}
                    </button>
                  </td>
                  <td className="muted" style={{ fontSize: "0.85rem" }}>
                    {b.period_start && b.period_end
                      ? `${b.period_start} → ${b.period_end}`
                      : "—"}
                  </td>
                  <td>{b.imported_count}</td>
                  <td>{b.skipped_count}</td>
                  <td>
                    {b.source !== "boekhoudgemak" && (
                      <label className="btn btn-ghost btn-sm" style={{ position: "relative" }}>
                        Attach PDF
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          aria-label={`Attach statement PDF to ${b.filename}`}
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            cursor: "pointer",
                          }}
                          onChange={(e) => {
                            const picked = e.target.files?.[0] || null;
                            e.target.value = "";
                            onAttachFile(b, picked).catch(console.error);
                          }}
                        />
                      </label>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <BottomNav />
    </div>
  );
}
